package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.LocaleProgressDto
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LProgressBar
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.progressTrailingLabel
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// What is left in this file is product vocabulary, not design system.
//
// `LocaleFlag` knows what a locale code looks like, `StateBadge` knows there are exactly three
// translation states, and `ProgressCell` knows a locale has translated and approved counts — all
// three are shaped by `dev.lingvo.shared` types that only this product has, so a second application
// built on the design system has no use for any of them.
//
// The four state screens that used to sit below them — `PageHeader`, `ErrorState`,
// `AsyncFailureState`, `LoadingState` — know nothing about Lingvo and are now `ds/LStates.kt`.

val LocaleFlagStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.HeadingSmall)
        .lineHeight(1)
        .styleModifier { property("display", "inline-flex") }
}

val ProgressCellStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minWidth(180.px)
        .gap(Space.MD)
}

@Composable
fun LocaleFlag(
    locale: String,
    modifier: Modifier = Modifier,
    /**
     * True where the language is already named in text beside the flag — then the flag is
     * decoration and announcing it repeats the row. False where the flag stands alone as the only
     * indication of which language a control refers to.
     */
    decorative: Boolean = false,
) {
    val info = localeInfo(locale)
    Span(
        LocaleFlagStyle.toModifier().then(modifier).toAttrs {
            if (decorative) {
                attr("aria-hidden", "true")
            } else {
                attr("role", "img")
                attr("aria-label", trf("common.locale_flag", "{language} flag", "language" to info.englishName))
                attr("title", info.englishName)
            }
        },
    ) {
        // Only where the locale names a country. For a language-only code the flag is a guess the
        // table had to make to fill the field — and this component is the one place it stood alone,
        // as the sole indication of which language a control refers to. The code is the honest
        // answer there, and the one the developer writes in `lingvo.yaml`.
        Text(localeFlagPrefix(info.code).ifBlank { info.code }.trim())
    }
}

@Composable
fun StateBadge(
    state: TranslationState,
    modifier: Modifier = Modifier,
) {
    val variant = when (state) {
        TranslationState.EMPTY -> LBadgeVariant.NEUTRAL
        TranslationState.TRANSLATED -> LBadgeVariant.INFO
        TranslationState.APPROVED -> LBadgeVariant.SUCCESS
    }
    LBadge(
        // **Written out, not derived from the enum name.** `state.name.lowercase()` produced the
        // label, so three words of the product's core vocabulary — the ones on every key in the
        // editor — were an identifier wearing a capital letter, reachable by no catalogue and no
        // translator. A `when` also fails to compile the day a fourth state is added, which is the
        // right moment to be told.
        text = when (state) {
            TranslationState.EMPTY -> t("common.empty", "Empty")
            TranslationState.TRANSLATED -> t("common.translated", "Translated")
            TranslationState.APPROVED -> t("common.approved", "Approved")
        },
        modifier = modifier,
        variant = variant,
    )
}

/**
 * @param showTrailingLabel print "40 left" to the right of the bar. False where the caller already
 *   states that number — the readiness row's own "Untranslated" counter is the identical value
 *   (`LProgressCounts.empty` is `total - translated - approved`, which is exactly `untranslated`),
 *   and printing it twice eight pixels apart made the reader parse one fact in two phrasings, once
 *   per language, on the densest block of the densest page in the product.
 */
@Composable
fun ProgressCell(
    progress: LocaleProgressDto,
    modifier: Modifier = Modifier,
    showTrailingLabel: Boolean = true,
) {
    val total = progress.total.coerceAtLeast(0)
    val approved = progress.approved.coerceIn(0, total)
    val completed = progress.translated.coerceIn(approved, total)
    val counts = LProgressCounts(
        total = total,
        translated = completed - approved,
        approved = approved,
    )
    Row(
        ProgressCellStyle.toModifier().then(modifier),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LProgressBar(
            progress = counts,
            modifier = Modifier.flexGrow(1),
            // The language, not the code: every visible element on this row says "German" while
            // this was announcing "de translation progress" — the one place `localeInfo` was not
            // applied was the one place a non-visual reader depends on it.
            label = trf(
                "common.locale_translation_progress",
                "{language} translation progress",
                "language" to localeInfo(progress.locale).englishName,
            ),
            showCounts = false,
        )
        if (showTrailingLabel) {
            Span(
                Modifier
                    .minWidth(72.px)
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Small)
                    .fontWeight(600)
                    .textAlign(TextAlign.End)
                    .tabularNumbers()
                    .toAttrs(),
            ) {
                Text(progressTrailingLabel(counts))
            }
        }
    }
}
